import { useState } from 'react';
import { WarningCircle } from '@phosphor-icons/react';
import { authApi } from '@/auth/api';

/**
 * RatingsBandRefresh — bandeau cyan d'invitation à reconnecter Lichess
 * (Stats Hub V2 PR #F, bug B2 ; cf. `docs/plans/2026-05-19-stats-hub-v2-design.md` §8).
 *
 * Affiché quand `auth.user.lichess_oauth_expired === true`. Léon a vu le 401
 * silencieux ("mes stats sont vides") sans comprendre pourquoi — ce bandeau
 * rend la cause visible et l'action évidente (1 clic relance le PKCE).
 *
 * Design (DESIGN.md §6) :
 *  - Tonalité cyan (cohérente avec les surfaces ratings/Lichess).
 *  - Icône Phosphor `WarningCircle` (icon, pas emoji — CLAUDE.md §5).
 *  - role="alert" pour les screen readers (CONVENTIONS.md §15 a11y).
 *  - Pas de dismiss : le bandeau doit rester tant que le flag est `true`.
 *
 * Flux : `authApi.startLichessOAuth()` est un POST qui renvoie `{auth_url}`
 * (verifier PKCE généré côté serveur, voir ARCHITECTURE.md §9). On redirige
 * ensuite via `window.location.assign(auth_url)`. Cohérent `LoginPage.tsx`
 * + `GameListPage.tsx`.
 */
export function RatingsBandRefresh(): JSX.Element {
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleReconnect = async (): Promise<void> => {
    setIsRedirecting(true);
    setError(null);
    try {
      const { auth_url } = await authApi.startLichessOAuth();
      window.location.assign(auth_url);
    } catch {
      setIsRedirecting(false);
      setError('Impossible de relancer la connexion Lichess. Réessaye dans un instant.');
    }
  };

  return (
    <aside
      role="alert"
      aria-label="Token Lichess expiré"
      data-testid="ratings-band-refresh"
      className="flex items-start gap-3 rounded-xl border border-cyan-500/40 bg-cyan-500/10 px-4 py-3"
    >
      <span
        aria-hidden="true"
        className="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg bg-cyan-500/20 text-cyan-500"
      >
        <WarningCircle size={18} weight="bold" />
      </span>
      <div className="flex flex-col gap-2 min-w-0 flex-1">
        <p
          className="font-display text-body-sm font-semibold text-cyan-500"
          style={{ letterSpacing: '-0.005em' }}
        >
          Connexion Lichess à rafraîchir
        </p>
        <p className="text-body-sm text-paper-50 leading-snug">
          Reconnecte ton compte Lichess pour rafraîchir tes statistiques.
        </p>
        <button
          type="button"
          onClick={() => {
            void handleReconnect();
          }}
          disabled={isRedirecting}
          data-testid="ratings-band-refresh-cta"
          className="inline-flex items-center self-start mt-1 px-3 py-1.5 text-body-sm font-mono uppercase tracking-wider rounded-md border border-cyan-500/60 text-cyan-500 hover:bg-cyan-500/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isRedirecting ? 'Redirection…' : 'Reconnecter'}
        </button>
        {error !== null && (
          <p className="text-body-sm text-coral-500" role="status">
            {error}
          </p>
        )}
      </div>
    </aside>
  );
}
